import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory} from "react-router-dom";
import {CSelect} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {getApi} from "../../apis/backendApis";
import useLoading from "../../services/useLoading";
import useAlert from "../../services/useAlert";
import {Evento, SituacaoItem, SituacaoProcesso, dataBr, erroDaApi, quandoEvento} from "./modelo";
import {COR_EVENTO} from "./calendario";
import {PessoasPapel, buscaPessoasPapel, opcoesDePessoas, textoSetores} from "./pessoasPapel";
import '../../scss/styles-processo.scss';

// ------------------------------------------------------------------ tipos (GET /processo/meus)

interface Tarefa {
    processo_id: number,
    cliente: string,
    fluxo: string,
    processo_titulo: string,
    etapa_id: number,
    etapa_ordem: number,
    etapa_nome: string,
    item_id: number,
    titulo: string,
    tipo: number,
    situacao: number,
    prazo: string,
    prazo_original: string,
    atrasado: boolean,
    dias_atraso: number,
    vencendo: boolean,
    em_revisao: boolean,
    duvidas: number,
    com_cliente: boolean,
    papel_codigo: string,
    papel_nome: string,
    /** Contato do cliente, quando o item e do cliente. */
    contato: string | null,
    anexos: number,
    /** Por que e meu: responsavel pelo item, pela etapa (item do cliente) ou gestor. */
    por?: 'item' | 'etapa' | 'gestor',
    responsavel?: string,
    /** Inicio do proximo evento agendado para o item (#484909). */
    agendado?: string | null,
}

interface PapelSemPessoa {
    processo_id: number,
    cliente: string,
    fluxo: string,
    papel_id: number,
    papel_codigo: string,
    papel_nome: string,
    etapas: string[],
    itens_pendentes: number,
    itens_atrasados: number,
    proximo_prazo: string | null,
}

interface EtapaAtrasada {
    processo_id: number,
    cliente: string,
    fluxo: string,
    etapa_id: number,
    etapa_ordem: number,
    etapa_nome: string,
    previsao_fim: string,
    dias_atraso: number,
    itens_atrasados: number,
    pendentes: number,
    percentual: number,
    responsavel: string | null,
    minha: boolean,
}

interface ResumoProcesso {
    processo_id: number,
    cliente: string,
    fluxo: string,
    processo_titulo: string,
    situacao: number,
    situacao_nome: string,
    percentual: number,
    previsao_fim: string,
    desvio_dias_uteis: number,
    atrasados: number,
    gestor: string,
    sou_gestor: boolean,
    minhas_tarefas: number,
    sem_pessoa?: number,
    cliente_esperando?: number,
}

interface Painel {
    hoje: string,
    usuario: {id: number, nome: string},
    tarefas: Tarefa[],
    gestao: {
        coordena: boolean,
        papeis_sem_pessoa: PapelSemPessoa[],
        itens_sem_responsavel: Tarefa[],
        etapas_atrasadas: EtapaAtrasada[],
        equipe_atrasados: Array<{usuario_id: number, responsavel: string, itens: Tarefa[]}>,
        processos: ResumoProcesso[],
    },
    processos: ResumoProcesso[],
    agenda: Evento[],
}

type Foco = 'cliente' | 'atrasados' | 'vencendo' | 'com_cliente';
type Aba = 'tarefas' | 'gestao';

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const somaDias = (iso: string, dias: number) => {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + dias);
    return d.toISOString().slice(0, 10);
};

const diaCurto = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return `${DIAS_SEMANA[d.getDay()]}, ${dataBr(iso).slice(0, 5)}`;
};

/** Dias corridos de hoje ate o dia. */
const diasAte = (hoje: string, iso: string) => Math.round((new Date(`${iso}T12:00:00`).getTime() - new Date(`${hoje}T12:00:00`).getTime()) / 86400000);

/** "atrasado 3 d.u." / "vence hoje" / "amanhã" / "qua, 15/10". */
const textoPrazo = (t: Tarefa, hoje: string) => {
    if (t.atrasado) return `atrasado ${t.dias_atraso} d.u.`;
    if (t.prazo === hoje) return 'vence hoje';
    if (t.prazo === somaDias(hoje, 1)) return 'vence amanhã';
    return `até ${diaCurto(t.prazo)}`;
};

const saudacao = () => {
    const hora = new Date().getHours();
    return hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
};

const clienteEsperando = (t: Tarefa) => t.em_revisao || t.duvidas > 0;

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * Meus Processos (#484909): a caixa de trabalho do dia a dia. Minhas tarefas
 * por urgencia (o cliente esperando primeiro) e, depois, os proximos prazos
 * como agenda (dia a dia, por processo); a gestao de quem coordena (alocar
 * quem falta, atrasos de etapa e da equipe) e, ao lado, a agenda de eventos e
 * os processos de que participo. O item abre na tela do processo, ja nele.
 */
const MeusProcessos = () => {
    const history = useHistory();
    const [setLoading] = useLoading();
    const [showAlert] = useAlert();

    const [painel, setPainel] = useState<Painel | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [aba, setAba] = useState<Aba>('tarefas');
    const [foco, setFoco] = useState<Foco | null>(null);
    const [busca, setBusca] = useState('');
    const [agrupar, setAgrupar] = useState<'prazo' | 'processo'>('prazo');
    const [equipeAberta, setEquipeAberta] = useState<Set<number>>(new Set());
    const [usuarios, setUsuarios] = useState<Array<{id: number, nome: string}>>([]);
    const [alocar, setAlocar] = useState<{[papelId: number]: string}>({});
    // Quem pode ocupar papel em cada processo: os setores do fluxo dele (#484909).
    const [pessoasPorProcesso, setPessoasPorProcesso] = useState<{[processoId: number]: PessoasPapel | null}>({});
    const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
    // A faixa de foco fica presa no topo; com a tela rolada, ganha sombra.
    const [rolou, setRolou] = useState(false);

    /** Recarrega o painel; "silencioso" nao cobre a tela (depois de uma acao). */
    const carregar = useCallback((silencioso = false) => {
        if (!silencioso) setLoading(true);
        return getApi().get('processo/meus')
            .then(resposta => {
                setPainel(resposta.data);
                setErro(null);
                setAtualizadoEm(new Date());
            })
            .catch(e => setErro(erroDaApi(e, 'Não foi possível carregar seus processos.')))
            .finally(() => { if (!silencioso) setLoading(false); });
    }, []);

    useEffect(() => {
        carregar();
        getApi().get('/usuarios/suggest').then(resposta => {
            setUsuarios((resposta.data.usuarios || []).map((par: any) => {
                const [id, nome] = Object.entries(par)[0];
                return {id: Number(id), nome: String(nome)};
            }));
        }).catch(() => setUsuarios([]));
    }, []);

    // Voltou para a aba do navegador: o que os outros fizeram aparece.
    useEffect(() => {
        const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(true); };
        document.addEventListener('visibilitychange', aoVoltar);
        return () => document.removeEventListener('visibilitychange', aoVoltar);
    }, [carregar]);

    const post = (rota: string, dados: any, arquivos: File[] = []) => {
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(dados));
        arquivos.forEach((arquivo, i) => corpo.append(`arquivo${i}`, arquivo));
        return getApi().post(rota, corpo);
    };

    /** Abre a tela do processo (?item=, ?etapa= ou ?aba=); o voltar de la traz de volta para ca. */
    const abrirProcesso = (id: number, pedido = '') => history.push({pathname: `/processos/${id}`, search: pedido, state: {voltar: '/processos/meus'}});

    const abrirItem = (t: Tarefa) => abrirProcesso(t.processo_id, `?item=${t.item_id}`);

    const alocarPapel = (p: PapelSemPessoa) => {
        setLoading(true);
        post(`processo/papel/pessoa/${p.papel_id}`, {usuario_id: Number(alocar[p.papel_id]), motivo: 'Alocação pelo Meus Processos: papel sem responsável.'})
            .then(() => {
                showAlert({msg: `${p.papel_nome} alocado em ${p.cliente}.`, type: 'success', toaster: true});
                setAlocar(atual => { const novo = {...atual}; delete novo[p.papel_id]; return novo; });
                return carregar(true);
            })
            .catch(e => showAlert({title: 'Não foi possível', msg: erroDaApi(e, 'Erro ao alocar.'), type: 'danger', toaster: true}))
            .finally(() => setLoading(false));
    };

    const assumir = (t: Tarefa) => {
        if (!painel) return;
        setLoading(true);
        post(`processo/item/transferir/${t.item_id}`, {usuario_id: painel.usuario.id, motivo: 'Assumido pelo Meus Processos: item sem responsável.'})
            .then(() => {
                showAlert({msg: `Você assumiu “${t.titulo}”.`, type: 'success', toaster: true});
                return carregar(true);
            })
            .catch(e => showAlert({title: 'Não foi possível', msg: erroDaApi(e, 'Erro ao assumir.'), type: 'danger', toaster: true}))
            .finally(() => setLoading(false));
    };

    // ------------------------------------------------------------------ dados derivados

    const termo = busca.trim().toLowerCase();
    const casa = (t: Tarefa) => !termo || `${t.cliente} ${t.titulo} ${t.etapa_nome} ${t.fluxo} ${t.processo_titulo}`.toLowerCase().includes(termo);

    const grupos = useMemo(() => {
        const tarefas = (painel?.tarefas || []).filter(casa);
        const esperando = tarefas.filter(clienteEsperando);
        const resto = tarefas.filter(t => !clienteEsperando(t));
        const meus = resto.filter(t => !t.com_cliente);
        return {
            todas: tarefas,
            cliente: esperando,
            atrasados: meus.filter(t => t.atrasado),
            vencendo: meus.filter(t => !t.atrasado && t.vencendo),
            proximos: meus.filter(t => !t.atrasado && !t.vencendo),
            com_cliente: resto.filter(t => t.com_cliente),
        };
    }, [painel, termo]);

    const gestao = painel?.gestao;

    // Busca uma vez por processo com papel sem pessoa.
    useEffect(() => {
        const faltam = Array.from(new Set((painel?.gestao.papeis_sem_pessoa || []).map(p => p.processo_id))).filter(id => !(id in pessoasPorProcesso));
        faltam.forEach(id => buscaPessoasPapel({processo_id: id})
            .then(dados => setPessoasPorProcesso(atual => ({...atual, [id]: dados})))
            .catch(() => setPessoasPorProcesso(atual => ({...atual, [id]: null}))));
    }, [painel]);
    const totalGestao = gestao
        ? gestao.papeis_sem_pessoa.length + gestao.itens_sem_responsavel.length + gestao.etapas_atrasadas.length
            + gestao.equipe_atrasados.reduce((s, g) => s + g.itens.length, 0)
        : 0;
    const resumoPorId = useMemo(() => new Map((painel?.processos || []).map(p => [p.processo_id, p])), [painel]);

    if (erro && !painel) {
        return <div className="pr-meus"><div className="pr-vazio">{erro}</div></div>;
    }
    if (!painel) {
        return <div className="pr-meus"/>;
    }
    const hoje = painel.hoje;

    // ------------------------------------------------------------------ pecas

    const chipPrazo = (t: Tarefa) => (
        <span className={`pr-chip ${t.atrasado ? 'atrasado' : t.vencendo ? 'vencendo' : ''}`}>
            <CIcon name="cil-clock"/> {textoPrazo(t, hoje)}
        </span>
    );

    /** naAgenda: dentro do dia e do processo, sem repetir o prazo e o cliente. */
    const linhaTarefa = (t: Tarefa, opcoes: {assumir?: boolean, naAgenda?: boolean} = {}) => (
        <li key={t.item_id} className={`pr-meus-tarefa ${t.atrasado ? 'atrasada' : t.vencendo ? 'vencendo' : ''}`}>
            <button type="button" className="pr-meus-tarefa-corpo" onClick={() => abrirItem(t)} title="Abrir o item na tela do processo">
                <span className="pr-meus-tarefa-titulo">{t.titulo}</span>
                <span className="pr-meus-tarefa-meta">
                    {!opcoes.naAgenda && <><strong>{t.cliente}</strong> · </>}
                    <span>{t.etapa_ordem}. {t.etapa_nome}</span>
                    {t.contato && <span> · com {t.contato}</span>}
                </span>
                <span className="pr-meus-tarefa-chips">
                    {!opcoes.naAgenda && chipPrazo(t)}
                    {t.duvidas > 0 && <span className="pr-chip duvida"><CIcon name="cil-speech"/> {t.duvidas === 1 ? 'dúvida do cliente' : `${t.duvidas} dúvidas`}</span>}
                    {t.em_revisao && <span className="pr-chip revisao">cliente respondeu: revisar</span>}
                    {t.situacao === SituacaoItem.EM_ANDAMENTO && !t.em_revisao && <span className="pr-chip sit-2">em andamento</span>}
                    {t.situacao === SituacaoItem.AGUARDANDO_CLIENTE && <span className="pr-chip cliente">pedido ao cliente</span>}
                    {t.agendado && <span className="pr-chip agendado"><CIcon name="cil-calendar"/> {quandoEvento(t.agendado)}</span>}
                    {t.anexos > 0 && <span className="pr-chip"><CIcon name="cil-paperclip"/> {t.anexos}</span>}
                    {t.por === 'etapa' && <span className="pr-chip" title="Item do cliente numa etapa sua: você acompanha">pela sua etapa</span>}
                    {t.por === 'gestor' && <span className="pr-chip" title="Item do cliente sem responsável pela etapa: o gestor acompanha">como gestor</span>}
                </span>
            </button>
            {opcoes.assumir && (
                <button type="button" className="pr-meus-acao" onClick={() => assumir(t)} title="O item passa para você">
                    <CIcon name="cil-user-follow"/> Assumir
                </button>
            )}
            <CIcon name="cil-chevron-right" className="pr-meus-seta"/>
        </li>
    );

    const secao = (chave: string, titulo: string, icone: string, lista: Tarefa[], dica?: string) => {
        if (lista.length === 0) return null;
        return (
            <section key={chave} className={`pr-meus-secao ${chave}`}>
                <header>
                    <CIcon name={icone}/>
                    <h5>{titulo}</h5>
                    <span className="pr-meus-conta">{lista.length}</span>
                    {dica && <small>{dica}</small>}
                </header>
                <ul>{lista.map(t => linhaTarefa(t))}</ul>
            </section>
        );
    };

    /** Proximos como agenda: dia a dia e, em cada dia, processo a processo. */
    const secaoProximos = () => {
        const lista = grupos.proximos;
        if (lista.length === 0) return null;
        const dias = new Map<string, Map<number, Tarefa[]>>();
        lista.forEach(t => {
            const dia = dias.get(t.prazo) || new Map<number, Tarefa[]>();
            dia.set(t.processo_id, [...(dia.get(t.processo_id) || []), t]);
            dias.set(t.prazo, dia);
        });
        return (
            <section key="proximos" className="pr-meus-secao proximos">
                <header>
                    <CIcon name="cil-calendar"/>
                    <h5>Próximos</h5>
                    <span className="pr-meus-conta">{lista.length}</span>
                    <small>Seus prazos dia a dia, por processo</small>
                </header>
                {Array.from(dias.entries()).map(([dia, processos]) => {
                    const data = new Date(`${dia}T12:00:00`);
                    const faltam = diasAte(hoje, dia);
                    return (
                        <div key={dia} className="pr-meus-agenda-dia">
                            <div className="pr-meus-agenda-data" title={dataBr(dia)}>
                                <small>{DIAS_SEMANA[data.getDay()]}</small>
                                <strong>{dia.slice(8, 10)}</strong>
                                <small>{MESES[data.getMonth()]}</small>
                            </div>
                            <div className="pr-meus-agenda-conteudo">
                                <span className="pr-meus-agenda-quando">{faltam === 1 ? 'amanhã' : `em ${faltam} dias`}</span>
                                {Array.from(processos.entries()).map(([processoId, tarefas]) => (
                                    <div key={processoId} className="pr-meus-agenda-processo">
                                        <button type="button" className="pr-meus-agenda-processo-nome" onClick={() => abrirProcesso(processoId)} title="Abrir o processo">
                                            <strong>{tarefas[0].cliente}</strong>
                                            <small>{tarefas[0].fluxo} · {tarefas[0].processo_titulo}</small>
                                        </button>
                                        <ul>{tarefas.map(t => linhaTarefa(t, {naAgenda: true}))}</ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </section>
        );
    };

    const FOCOS: Array<[Foco, string, string]> = [
        ['cliente', 'Cliente esperando', 'cil-speech'],
        ['atrasados', 'Atrasados', 'cil-warning'],
        ['vencendo', 'Até 2 dias úteis', 'cil-clock'],
        ['com_cliente', 'Com o cliente', 'cil-user'],
    ];

    const renderPorPrazo = () => {
        const mostra = (f: Foco) => foco === null || foco === f;
        const vazio = grupos.todas.length === 0;
        return (
            <>
                {mostra('cliente') && secao('cliente', 'O cliente está esperando você', 'cil-speech', grupos.cliente, 'Respostas e dúvidas que chegaram pelo portal')}
                {mostra('atrasados') && secao('atrasados', 'Atrasados', 'cil-warning', grupos.atrasados)}
                {mostra('vencendo') && secao('vencendo', 'Até 2 dias úteis', 'cil-clock', grupos.vencendo)}
                {mostra('com_cliente') && secao('com_cliente', 'Com o cliente', 'cil-user', grupos.com_cliente, 'O cliente precisa agir; você acompanha e cobra')}
                {foco === null && secaoProximos()}
                {vazio && (
                    <div className="pr-meus-vazio">
                        <CIcon name="cil-check-circle"/>
                        <strong>{termo ? 'Nada com essa busca.' : 'Tudo em dia.'}</strong>
                        <span>{termo ? 'Tente outro cliente, etapa ou item.' : 'Nenhum item pendente com você nos processos em aberto.'}</span>
                    </div>
                )}
                {!vazio && foco !== null && grupos[foco].length === 0 && <div className="pr-vazio">Nada aqui agora.</div>}
            </>
        );
    };

    const renderPorProcesso = () => {
        const porProcesso = new Map<number, Tarefa[]>();
        grupos.todas.filter(t => foco === null || (grupos[foco] as Tarefa[]).includes(t)).forEach(t => {
            porProcesso.set(t.processo_id, [...(porProcesso.get(t.processo_id) || []), t]);
        });
        if (porProcesso.size === 0) {
            return <div className="pr-vazio">{termo ? 'Nada com essa busca.' : 'Nenhum item pendente com você.'}</div>;
        }
        return Array.from(porProcesso.entries()).map(([id, lista]) => {
            const resumo = resumoPorId.get(id);
            return (
                <section key={id} className="pr-meus-secao processo">
                    <header>
                        <button type="button" className="pr-meus-processo-nome" onClick={() => abrirProcesso(id)} title="Abrir o processo">
                            <h5>{lista[0].cliente}</h5>
                            <small>{lista[0].fluxo} · {lista[0].processo_titulo}</small>
                        </button>
                        {resumo && (
                            <span className="pr-meus-processo-pct">
                                <span className="pr-barra"><span style={{width: `${resumo.percentual}%`}}/></span>
                                {Math.round(resumo.percentual)}%
                            </span>
                        )}
                        <span className="pr-meus-conta">{lista.length}</span>
                    </header>
                    <ul>{lista.map(t => linhaTarefa(t))}</ul>
                </section>
            );
        });
    };

    const renderGestao = () => {
        if (!gestao) return null;
        const nada = totalGestao === 0;
        return (
            <>
                {gestao.processos.length > 0 && (
                    <section className="pr-meus-secao processos">
                        <header>
                            <CIcon name="cil-layers"/>
                            <h5>Processos que você gere</h5>
                            <span className="pr-meus-conta">{gestao.processos.length}</span>
                        </header>
                        <div className="pr-meus-saude">
                            {gestao.processos.map(p => (
                                <button type="button" key={p.processo_id} className="pr-meus-saude-cartao" onClick={() => abrirProcesso(p.processo_id)}>
                                    <span className="pr-meus-saude-topo">
                                        <strong>{p.cliente}</strong>
                                        <span className={`pr-chip ${p.situacao === SituacaoProcesso.CONGELADO ? 'congelado' : ''}`}>{p.situacao_nome}</span>
                                    </span>
                                    <small>{p.fluxo} · término {dataBr(p.previsao_fim).slice(0, 5)}{p.desvio_dias_uteis > 0 ? ` (+${p.desvio_dias_uteis} d.u.)` : ''}</small>
                                    <span className="pr-progresso">
                                        <span className="pr-barra"><span style={{width: `${p.percentual}%`}}/></span>
                                        <strong>{Math.round(p.percentual)}%</strong>
                                    </span>
                                    <span className="pr-meus-tarefa-chips">
                                        {p.atrasados > 0 && <span className="pr-chip atrasado">{plural(p.atrasados, 'atrasado', 'atrasados')}</span>}
                                        {(p.sem_pessoa ?? 0) > 0 && <span className="pr-chip">{plural(p.sem_pessoa ?? 0, 'papel sem pessoa', 'papéis sem pessoa')}</span>}
                                        {(p.cliente_esperando ?? 0) > 0 && <span className="pr-chip revisao">{p.cliente_esperando} cliente esperando</span>}
                                        {p.atrasados === 0 && !p.sem_pessoa && !p.cliente_esperando && <span className="pr-chip sit-4">em dia</span>}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {gestao.itens_sem_responsavel.length > 0 && (
                    <section className="pr-meus-secao alocar">
                        <header>
                            <CIcon name="cil-user-follow"/>
                            <h5>Itens sem responsável nas suas etapas</h5>
                            <span className="pr-meus-conta">{gestao.itens_sem_responsavel.length}</span>
                            <small>Assuma, ou abra o item e transfira para alguém</small>
                        </header>
                        <ul>{gestao.itens_sem_responsavel.map(t => linhaTarefa(t, {assumir: true}))}</ul>
                    </section>
                )}

                {gestao.etapas_atrasadas.length > 0 && (
                    <section className="pr-meus-secao atrasados">
                        <header>
                            <CIcon name="cil-warning"/>
                            <h5>Etapas atrasadas</h5>
                            <span className="pr-meus-conta">{gestao.etapas_atrasadas.length}</span>
                        </header>
                        <ul>
                            {gestao.etapas_atrasadas.map(e => (
                                <li key={e.etapa_id} className="pr-meus-tarefa atrasada">
                                    <button type="button" className="pr-meus-tarefa-corpo" onClick={() => abrirProcesso(e.processo_id, `?etapa=${e.etapa_id}`)} title="Abrir a etapa">
                                        <span className="pr-meus-tarefa-titulo">{e.etapa_ordem}. {e.etapa_nome}</span>
                                        <span className="pr-meus-tarefa-meta">
                                            <strong>{e.cliente}</strong><span> · {e.fluxo} · {e.minha ? 'responsável: você' : `responsável: ${e.responsavel || 'ninguém'}`}</span>
                                        </span>
                                        <span className="pr-meus-tarefa-chips">
                                            {e.dias_atraso > 0
                                                ? <span className="pr-chip atrasado">fim previsto {dataBr(e.previsao_fim).slice(0, 5)} · {e.dias_atraso} d.u. de atraso</span>
                                                : <span className="pr-chip">fim previsto {dataBr(e.previsao_fim).slice(0, 5)}</span>}
                                            {e.itens_atrasados > 0 && <span className="pr-chip atrasado">{plural(e.itens_atrasados, 'item atrasado', 'itens atrasados')}</span>}
                                            <span className="pr-chip">{plural(e.pendentes, 'pendente', 'pendentes')} · {Math.round(e.percentual)}%</span>
                                        </span>
                                    </button>
                                    <CIcon name="cil-chevron-right" className="pr-meus-seta"/>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {gestao.equipe_atrasados.length > 0 && (
                    <section className="pr-meus-secao equipe">
                        <header>
                            <CIcon name="cil-people"/>
                            <h5>Atrasos da equipe</h5>
                            <span className="pr-meus-conta">{gestao.equipe_atrasados.reduce((s, g) => s + g.itens.length, 0)}</span>
                            <small>Itens atrasados com outras pessoas nos processos e etapas que você coordena</small>
                        </header>
                        {gestao.equipe_atrasados.map(g => {
                            const aberta = equipeAberta.has(g.usuario_id);
                            return (
                                <div key={g.usuario_id} className="pr-meus-pessoa">
                                    <button type="button" className="pr-meus-pessoa-cab" onClick={() => {
                                        const novo = new Set(equipeAberta);
                                        aberta ? novo.delete(g.usuario_id) : novo.add(g.usuario_id);
                                        setEquipeAberta(novo);
                                    }}>
                                        <span className="pr-meus-avatar">{g.responsavel.charAt(0)}</span>
                                        <span>{g.responsavel}</span>
                                        <span className="pr-chip atrasado">{g.itens.length} atrasado{g.itens.length > 1 ? 's' : ''}</span>
                                        <CIcon name={aberta ? 'cil-chevron-top' : 'cil-chevron-bottom'}/>
                                    </button>
                                    {aberta && <ul>{g.itens.map(t => linhaTarefa(t))}</ul>}
                                </div>
                            );
                        })}
                    </section>
                )}

                {gestao.papeis_sem_pessoa.length > 0 && (
                    <section className="pr-meus-secao alocar">
                        <header>
                            <CIcon name="cil-user-follow"/>
                            <h5>Etapas sem responsável</h5>
                            <span className="pr-meus-conta">{gestao.papeis_sem_pessoa.length}</span>
                            <small>A pessoa escolhida assume o papel no processo: as etapas e os itens dele</small>
                        </header>
                        <ul>
                            {gestao.papeis_sem_pessoa.map(p => (
                                <li key={p.papel_id} className="pr-meus-alocar">
                                    <div className="pr-meus-alocar-texto">
                                        <span className="pr-meus-tarefa-titulo">{p.papel_nome} <em>({p.papel_codigo})</em></span>
                                        <span className="pr-meus-tarefa-meta"><strong>{p.cliente}</strong> · {p.fluxo}</span>
                                        <span className="pr-meus-tarefa-chips">
                                            {p.etapas.map(e => <span key={e} className="pr-chip">{e}</span>)}
                                            <span className="pr-chip">{plural(p.itens_pendentes, 'item pendente', 'itens pendentes')}</span>
                                            {p.itens_atrasados > 0 && <span className="pr-chip atrasado">{plural(p.itens_atrasados, 'atrasado', 'atrasados')}</span>}
                                            {p.proximo_prazo && <span className="pr-chip"><CIcon name="cil-clock"/> próximo prazo {dataBr(p.proximo_prazo).slice(0, 5)}</span>}
                                            {pessoasPorProcesso[p.processo_id]?.restrito && (
                                                <span className="pr-chip" title={textoSetores(pessoasPorProcesso[p.processo_id])}>
                                                    <CIcon name="cil-people"/> {pessoasPorProcesso[p.processo_id]!.setores.map(s => s.nome).join(', ')}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="pr-meus-alocar-acao">
                                        <CSelect value={alocar[p.papel_id] || ''} onChange={(e: any) => setAlocar({...alocar, [p.papel_id]: e.target.value})}
                                                 aria-label={`Pessoa para ${p.papel_nome}`}>
                                            <option value="">Escolha a pessoa…</option>
                                            {opcoesDePessoas(pessoasPorProcesso[p.processo_id], usuarios).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                                        </CSelect>
                                        <button type="button" className="pr-meus-acao primaria" disabled={!alocar[p.papel_id]} onClick={() => alocarPapel(p)}>Alocar</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {nada && gestao.processos.length === 0 && (
                    <div className="pr-meus-vazio">
                        <CIcon name="cil-check-circle"/>
                        <strong>Nada para coordenar agora.</strong>
                        <span>Sem etapa sem responsável e sem atraso nas etapas e processos que você coordena.</span>
                    </div>
                )}
            </>
        );
    };

    const renderAgenda = () => {
        const porDia = new Map<string, Evento[]>();
        painel.agenda.forEach(e => {
            const dia = e.inicio.slice(0, 10);
            porDia.set(dia, [...(porDia.get(dia) || []), e]);
        });
        const nomeDia = (dia: string) => (dia === hoje ? 'Hoje' : dia === somaDias(hoje, 1) ? 'Amanhã' : diaCurto(dia));
        return (
            <section className="pr-bloco pr-meus-lado-bloco">
                <div className="pr-bloco-titulo">Agenda{painel.agenda.length > 0 ? ` · ${painel.agenda.length}` : ''}</div>
                {painel.agenda.length === 0 && <p className="pr-ajuda">Nenhum evento agendado com você.</p>}
                {Array.from(porDia.entries()).map(([dia, eventos]) => (
                    <div key={dia} className="pr-meus-dia">
                        <span className={`pr-meus-dia-nome ${dia === hoje ? 'hoje' : ''}`}>{nomeDia(dia)}</span>
                        {eventos.map(e => (
                            <button type="button" key={e.id} className="pr-meus-evento"
                                    onClick={() => e.processo_id && abrirProcesso(e.processo_id, e.item_id ? `?item=${e.item_id}` : `?evento=${e.id}`)}>
                                <span className="pr-agenda-cor" style={{background: COR_EVENTO[e.tipo]}}/>
                                <span className="pr-meus-evento-hora">{e.inicio.slice(11, 16)}</span>
                                <span className="pr-meus-evento-texto">
                                    <strong>{e.titulo}</strong>
                                    <small>{e.cliente} · {e.tipo_nome}{e.item_titulo && e.item_titulo !== e.titulo ? ` · ${e.item_titulo}` : ''}</small>
                                </span>
                            </button>
                        ))}
                    </div>
                ))}
            </section>
        );
    };

    const renderParticipo = () => (
        <section className="pr-bloco pr-meus-lado-bloco">
            <div className="pr-bloco-titulo">Processos em que estou · {painel.processos.length}</div>
            {painel.processos.length === 0 && <p className="pr-ajuda">Você não está em nenhum processo em aberto.</p>}
            {painel.processos.map(p => (
                <button type="button" key={p.processo_id} className="pr-meus-participo" onClick={() => abrirProcesso(p.processo_id)}>
                    <span className="pr-meus-participo-topo">
                        <strong>{p.cliente}</strong>
                        {p.minhas_tarefas > 0 && <span className="pr-meus-conta">{p.minhas_tarefas}</span>}
                    </span>
                    <small>{p.fluxo} · {p.sou_gestor ? 'você é o gestor' : `gestor ${p.gestor}`}{p.situacao === SituacaoProcesso.CONGELADO ? ' · congelado' : ''}</small>
                    <span className="pr-barra fina"><span style={{width: `${p.percentual}%`}}/></span>
                </button>
            ))}
        </section>
    );

    const primeiroNome = painel.usuario.nome.split(' ')[0];
    const total = painel.tarefas.length;
    const emProcessos = new Set(painel.tarefas.map(t => t.processo_id)).size;

    return (
        <div className="pr-meus" onScroll={e => setRolou(e.currentTarget.scrollTop > 40)}>
            <header className="pr-meus-topo">
                <div className="pr-meus-titulo">
                    <h4>Meus processos</h4>
                    <span>
                        {saudacao()}, {primeiroNome}. {total === 0
                            ? 'Nada pendente com você agora.'
                            : `Você tem ${total} ${total === 1 ? 'tarefa' : 'tarefas'} em ${emProcessos} ${emProcessos === 1 ? 'processo' : 'processos'}.`}
                    </span>
                </div>
                <div className="pr-meus-ferramentas">
                    <label className="pr-meus-busca">
                        <CIcon name="cil-magnifying-glass"/>
                        <input type="search" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente, etapa ou item" aria-label="Buscar"/>
                    </label>
                    {aba === 'tarefas' && (
                        <div className="pr-presets" role="group" aria-label="Agrupar">
                            <button type="button" className={agrupar === 'prazo' ? 'ativo' : ''} onClick={() => setAgrupar('prazo')}>Por prazo</button>
                            <button type="button" className={agrupar === 'processo' ? 'ativo' : ''} onClick={() => setAgrupar('processo')}>Por processo</button>
                        </div>
                    )}
                    <button type="button" className="pr-voltar" onClick={() => carregar()}
                            title={atualizadoEm ? `Atualizar (atualizado às ${atualizadoEm.toTimeString().slice(0, 5)})` : 'Atualizar'}>
                        <CIcon name="cil-reload"/>
                    </button>
                </div>
            </header>

            <div className={`pr-meus-focos ${rolou ? 'fixo' : ''}`} role="group" aria-label="Filtrar por urgência">
                {FOCOS.map(([chave, rotulo, icone]) => (
                    <button type="button" key={chave} className={`pr-meus-foco ${chave} ${foco === chave ? 'ativo' : ''} ${grupos[chave].length === 0 ? 'zerado' : ''}`}
                            onClick={() => { setAba('tarefas'); setFoco(foco === chave ? null : chave); }} aria-pressed={foco === chave}>
                        <CIcon name={icone}/>
                        <span className="pr-meus-foco-rotulo">{rotulo}</span>
                        <strong>{grupos[chave].length}</strong>
                    </button>
                ))}
                {gestao?.coordena && (
                    <button type="button" className={`pr-meus-foco alocar ${aba === 'gestao' ? 'ativo' : ''} ${totalGestao === 0 ? 'zerado' : ''}`}
                            onClick={() => { setFoco(null); setAba(aba === 'gestao' ? 'tarefas' : 'gestao'); }}>
                        <CIcon name="cil-people"/>
                        <span className="pr-meus-foco-rotulo">Para coordenar</span>
                        <strong>{totalGestao}</strong>
                    </button>
                )}
            </div>

            <div className="pr-meus-corpo">
                <main className="pr-meus-principal">
                    {gestao?.coordena && (
                        <nav className="pr-meus-abas">
                            <button type="button" className={aba === 'tarefas' ? 'ativa' : ''} onClick={() => setAba('tarefas')}>
                                Minhas tarefas <span>{grupos.todas.length}</span>
                            </button>
                            <button type="button" className={aba === 'gestao' ? 'ativa' : ''} onClick={() => { setFoco(null); setAba('gestao'); }}>
                                Gestão <span>{totalGestao}</span>
                            </button>
                        </nav>
                    )}
                    {foco && aba === 'tarefas' && (
                        <div className="pr-meus-filtro-ativo">
                            Mostrando só: <strong>{FOCOS.find(f => f[0] === foco)?.[1]}</strong>
                            <button type="button" className="pr-link" onClick={() => setFoco(null)}>ver tudo</button>
                        </div>
                    )}
                    {aba === 'tarefas' && (agrupar === 'prazo' ? renderPorPrazo() : renderPorProcesso())}
                    {aba === 'gestao' && renderGestao()}
                </main>
                <aside className="pr-meus-lado">
                    {renderAgenda()}
                    {renderParticipo()}
                </aside>
            </div>
        </div>
    );
};

export default MeusProcessos;
